/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t, Trans } from '@lingui/macro'
import { Typography } from '@mui/material'
import { useCoordinatorAssignments } from '@om1/aspen-api/codegen/hooks/useCoordinatorAssignments'
import { useDashboardMetrics } from '@om1/aspen-api/codegen/hooks/useDashboardMetrics'
import { useRecordRetrievalStatus } from '@om1/aspen-api/codegen/hooks/useRecordRetrievalStatus'
import { useStatusBreakdown } from '@om1/aspen-api/codegen/hooks/useStatusBreakdown'
import { useSubjectLocations } from '@om1/aspen-api/codegen/hooks/useSubjectLocations'
import { Card, Col, Row } from 'antd'
import { HeartPulse, LayoutDashboard, UserPlus, Users } from 'lucide-react'
import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useAspenAuth } from '../providers/Auth0AspenProvider'
import { AspenRoutes } from '../routes'
import ReferrerWidget from './CandidateReferrerWidget'
import CoordinatorCards from './CoordinatorCards'
import StatusWidget, { StatusCategory } from './StatusWidget'
import { getStatusColor, SubjectBreakdownContent } from './SubjectCharts'
import UsaStateMap from './UsStateMap'

// Styled card component for consistent styling
export const DashboardCard = ({ children, onClick, className = '' }: { children: React.ReactNode; onClick?: () => void; className?: string }) => {
    const baseClasses = 'border border-gray-200 shadow-sm transition-shadow bg-white'
    const hoverClasses = onClick ? 'cursor-pointer hover:shadow-md' : ''

    return (
        <Card className={`${baseClasses} ${hoverClasses} ${className}`} onClick={onClick} hoverable={!!onClick}>
            {children}
        </Card>
    )
}

const MetricCardContent = ({ label, value, icon }: { label: React.ReactNode; value: React.ReactNode; icon: React.ReactNode }) => {
    return (
        <div className='flex items-start justify-between'>
            <div>
                <p className='text-sm font-medium text-gray-600'>{label}</p>
                {typeof value === 'number' ? <p className='text-3xl font-bold mt-1 text-gray-900'>{value}</p> : value}
            </div>
            <div className='p-2.5 rounded-lg bg-primary-main/10 text-primary-main'>{icon}</div>
        </div>
    )
}

const DashboardPage = () => {
    const { user } = useAspenAuth()
    const history = useHistory()
    // const [pendingCandidates, setPendingCandidates] = useState<CandidateDTO[]>([])
    // const [enrolledPatients, setEnrolledPatients] = useState<PatientDTO[]>([])
    // const [overdueCandidates, setOverdueCandidates] = useState<CandidateDTO[]>([])
    const { metrics: dashboardMetrics } = useDashboardMetrics()
    const { status: recordRetrievalStatus } = useRecordRetrievalStatus()
    const { assignments: coordinatorAssignments } = useCoordinatorAssignments()
    const { items: candidateStatusBreakdown, loading: candidateStatusBreakdownLoading } = useStatusBreakdown('candidate')
    const { items: carePartnerStatusBreakdown, loading: carePartnerStatusBreakdownLoading } = useStatusBreakdown('carePartner')
    const { items: patientSubjectLocations } = useSubjectLocations('patient')
    const candidateBreakdownFromApi = useMemo(
        () => ({
            key: 'candidates' as const,
            title: t`Candidate Breakdown`,
            data: candidateStatusBreakdown.map((item) => ({
                name: item.status,
                value: item.count,
                fill: getStatusColor(item.status),
                percentage: item.percentage
            }))
        }),
        [candidateStatusBreakdown]
    )
    const carePartnerBreakdownFromApi = useMemo(
        () => ({
            key: 'carePartners' as const,
            title: t`Care Partner Breakdown`,
            data: carePartnerStatusBreakdown.map((item) => ({
                name: item.status,
                value: item.count,
                fill: getStatusColor(item.status),
                percentage: item.percentage
            }))
        }),
        [carePartnerStatusBreakdown]
    )
    const patientStateData = useMemo(() => {
        return patientSubjectLocations.reduce<Record<string, number>>((acc, item) => {
            acc[item.stateName] = item.count
            return acc
        }, {})
    }, [patientSubjectLocations])

    const statusCategories = useMemo<StatusCategory[]>(() => {
        const successful = recordRetrievalStatus?.successful ?? 0
        const needsAttention = recordRetrievalStatus?.needsAttention ?? 0
        const failed = recordRetrievalStatus?.failed ?? 0
        const notStarted = recordRetrievalStatus?.notStarted ?? 0
        const total = successful + needsAttention + failed + notStarted
        const toPercent = (count: number) => (total > 0 ? Number(((count / total) * 100).toFixed(2)) : 0)

        return [
            { label: 'Successful', count: successful, percentage: toPercent(successful), color: 'chart-success' },
            { label: 'Needs Attention', count: needsAttention, percentage: toPercent(needsAttention), color: 'chart-attention' },
            { label: 'Failed', count: failed, percentage: toPercent(failed), color: 'chart-failed' },
            { label: 'Not Started', count: notStarted, percentage: toPercent(notStarted), color: 'chart-neutral' }
        ]
    }, [recordRetrievalStatus])

    return (
        <div className='flex flex-col h-[calc(100vh-100px)]'>
            <div className='px-8 py-6 flex flex-col h-full overflow-auto'>
                <div className='flex items-center mb-6 flex-shrink-0'>
                    <div className='bg-primary-main text-white p-1.5 rounded-full flex-shrink-0 mr-3'>
                        <LayoutDashboard size={20} />
                    </div>
                    <div>
                        <Typography variant='h5' className='font-bold text-gray-800'>
                            <Trans>Central Site Dashboard</Trans>
                        </Typography>
                        <div className='text-sm text-gray-500 mt-1'>
                            <span>{user?.org_name}</span>
                        </div>
                    </div>
                </div>

                {/* Summary Stats */}
                <Row gutter={[16, 16]} className='mb-8'>
                    <Col xs={24} md={12} lg={8}>
                        <DashboardCard onClick={() => history.push(AspenRoutes.CANDIDATES)}>
                            <MetricCardContent label={<Trans>Candidates</Trans>} value={dashboardMetrics?.candidates} icon={<UserPlus size={20} />} />
                        </DashboardCard>
                    </Col>
                    <Col xs={24} md={12} lg={8}>
                        <DashboardCard onClick={() => history.push(AspenRoutes.PATIENTS)}>
                            <MetricCardContent label={<Trans>Patients</Trans>} value={dashboardMetrics?.patients} icon={<HeartPulse size={20} />} />
                        </DashboardCard>
                    </Col>
                    <Col xs={24} md={12} lg={8}>
                        <DashboardCard onClick={() => history.push(AspenRoutes.CAREPARTNERS)}>
                            <MetricCardContent
                                label={<Trans>Care Partners</Trans>}
                                value={dashboardMetrics?.carePartners}
                                icon={<Users size={20} />}
                            />
                        </DashboardCard>
                    </Col>
                </Row>
                <Row gutter={[16, 16]} className='mb-8'>
                    <Col xs={24} md={12} lg={8}>
                        <DashboardCard>
                            <p className='text-base font-semibold text-gray-700 mb-4'>{candidateBreakdownFromApi.title}</p>
                            {candidateStatusBreakdownLoading ? (
                                <p className='text-sm text-gray-500'>Loading...</p>
                            ) : (
                                <SubjectBreakdownContent breakdown={candidateBreakdownFromApi} />
                            )}
                        </DashboardCard>
                    </Col>
                    <Col xs={24} md={12} lg={8}>
                        <DashboardCard>
                            <p className='text-base font-semibold text-gray-700 mb-4'>{carePartnerBreakdownFromApi.title}</p>
                            {carePartnerStatusBreakdownLoading ? (
                                <p className='text-sm text-gray-500'>Loading...</p>
                            ) : (
                                <SubjectBreakdownContent breakdown={carePartnerBreakdownFromApi} />
                            )}
                        </DashboardCard>
                    </Col>
                    <Col xs={24} md={12} lg={8}>
                        <ReferrerWidget />
                    </Col>
                </Row>
                <Row gutter={[16, 16]} className='mb-8'>
                    <Col xs={24} md={24} lg={16}>
                        <DashboardCard>
                            <CoordinatorCards coordinators={coordinatorAssignments} />
                        </DashboardCard>
                    </Col>
                    <Col xs={24} md={24} lg={8}>
                        <DashboardCard>
                            <StatusWidget statusCategories={statusCategories} />
                        </DashboardCard>
                    </Col>
                </Row>
                <Row gutter={[16, 16]} className='mb-8'>
                    <Col xs={24} md={24} lg={24}>
                        <DashboardCard className='min-h-[620px]'>
                            <h3 className='text-sm font-semibold text-card-foreground mb-2'>
                                <Trans>Geographic Distribution</Trans>
                            </h3>
                            <p className='text-xs text-muted-foreground mb-4'>
                                <Trans>Patient count by state across all 50 states + DC</Trans>
                            </p>
                            <UsaStateMap data={patientStateData} />
                        </DashboardCard>
                    </Col>
                </Row>
            </div>
        </div>
    )
}

export default DashboardPage
